import { doc, getDoc, setDoc, updateDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './config';
import { UserProfile, Language } from '../types/user';

export const createUserProfile = async (
  uid: string,
  email: string,
  language: Language = 'en',
  age?: number,
  country?: string,
  city?: string
): Promise<UserProfile> => {
  const userProfile: UserProfile = {
    uid,
    email,
    username: email.split('@')[0], // Username par défaut
    isAdmin: false,
    plan: 'free',
    language,  // Utilise la langue fournie
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    registrationDate: new Date().toISOString(),
    lastLoginDate: new Date().toISOString(),
    // Initialisation des champs pour la gestion des limites de rêves
    dreamCount: 0,
    monthlyDreamCount: 0,
    lastDreamCountReset: new Date().toISOString(),
    weeklyDreamCount: 0,
    lastWeeklyCountReset: new Date().toISOString(),
  };

  if (typeof age === 'number') {
    userProfile.age = age;
  }
  if (country) {
    userProfile.country = country;
  }
  if (city) {
    userProfile.city = city;
  }

  await setDoc(doc(db, 'users', uid), userProfile);
  return userProfile;
};

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (userDoc.exists()) {
    return userDoc.data() as UserProfile;
  }
  return null;
};

export const updateUserProfile = async (
  uid: string, 
  updates: Partial<UserProfile>
): Promise<void> => {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, {
    ...updates,
    lastLoginDate: new Date().toISOString(),
  });
};

export const updateUserPlan = async (
  uid: string, 
  plan: UserProfile['plan']
): Promise<void> => {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, { plan });
};

/**
 * Supprime le profil utilisateur et toutes ses données associées
 * @param uid Identifiant de l'utilisateur
 */
export const deleteUserProfile = async (uid: string): Promise<void> => {
  try {
    console.log(`Début de la suppression du compte utilisateur ${uid}`);
    
    // 1. Récupérer les informations utilisateur pour obtenir le stripeCustomerId
    const userProfile = await getUserProfile(uid);
    console.log(`Profil utilisateur récupéré:`, userProfile);
    
    // 2. Si l'utilisateur a un abonnement Stripe, on le stocke pour le traitement ultérieur
    if (userProfile?.stripeCustomerId) {
      console.log(`Enregistrement des informations d'abonnement Stripe pour annulation ultérieure`);
      try {
        const cancelSubscriptionRef = doc(db, 'subscriptionCancellations', uid);
        await setDoc(cancelSubscriptionRef, {
          stripeCustomerId: userProfile.stripeCustomerId,
          subscriptionId: userProfile.subscriptionId,
          email: userProfile.email,
          cancelledAt: new Date().toISOString(),
          processed: false
        });
        console.log(`Informations d'abonnement enregistrées pour traitement ultérieur`);
      } catch (error) {
        console.error(`Erreur lors de l'enregistrement des informations d'abonnement:`, error);
        // Continuer même si cette étape échoue
      }
    }
    
    // 3. Supprimer les rêves dans la collection principale
    try {
      console.log(`Suppression des rêves dans la collection principale...`);
      const dreamsRef = collection(db, 'dreams');
      const dreamsQuery = query(dreamsRef, where('userId', '==', uid));
      const dreamsSnapshot = await getDocs(dreamsQuery);
      
      console.log(`${dreamsSnapshot.docs.length} rêves trouvés dans la collection principale`);
      
      // Supprimer les rêves un par un
      for (const dreamDoc of dreamsSnapshot.docs) {
        try {
          console.log(`Suppression du rêve ${dreamDoc.id}`);
          await deleteDoc(dreamDoc.ref);
          console.log(`Rêve ${dreamDoc.id} supprimé avec succès`);
        } catch (error) {
          console.error(`Erreur lors de la suppression du rêve ${dreamDoc.id}:`, error);
          // Continuer avec les autres rêves même si un échoue
        }
      }
      
      console.log(`Suppression des rêves terminée`);
    } catch (error) {
      console.error(`Erreur lors de la suppression des rêves:`, error);
      // Continuer même si cette étape échoue
    }
    
    // 4. Supprimer les sous-collections de l'utilisateur
    try {
      console.log(`Suppression des sous-collections de l'utilisateur...`);
      
      // Liste des sous-collections à supprimer
      const subCollections = ['dreams']; // Ajouter d'autres sous-collections si nécessaire
      
      for (const subCollectionName of subCollections) {
        const subCollectionRef = collection(db, 'users', uid, subCollectionName);
        const subCollectionSnapshot = await getDocs(subCollectionRef);
        
        console.log(`${subCollectionSnapshot.docs.length} documents trouvés dans la sous-collection ${subCollectionName}`);
        
        for (const subDoc of subCollectionSnapshot.docs) {
          try {
            console.log(`Suppression du document ${subDoc.id} dans la sous-collection ${subCollectionName}`);
            await deleteDoc(subDoc.ref);
            console.log(`Document ${subDoc.id} supprimé avec succès`);
          } catch (error) {
            console.error(`Erreur lors de la suppression du document ${subDoc.id}:`, error);
            // Continuer avec les autres documents même si un échoue
          }
        }
      }
      
      console.log(`Suppression des sous-collections terminée`);
    } catch (error) {
      console.error(`Erreur lors de la suppression des sous-collections:`, error);
      // Continuer même si cette étape échoue
    }
    
    // 5. Vérifier les références à l'utilisateur dans d'autres collections
    try {
      console.log(`Vérification des références à l'utilisateur dans d'autres collections...`);
      
      // Liste des collections où l'utilisateur pourrait être référencé
      const collections = ['settings', 'analysis_categories']; // Ajouter d'autres collections si nécessaire
      
      for (const collectionName of collections) {
        const collectionRef = collection(db, collectionName);
        const collectionQuery = query(collectionRef, where('userId', '==', uid));
        const collectionSnapshot = await getDocs(collectionQuery);
        
        console.log(`${collectionSnapshot.docs.length} références trouvées dans la collection ${collectionName}`);
        
        for (const refDoc of collectionSnapshot.docs) {
          try {
            console.log(`Suppression de la référence ${refDoc.id} dans la collection ${collectionName}`);
            await deleteDoc(refDoc.ref);
            console.log(`Référence ${refDoc.id} supprimée avec succès`);
          } catch (error) {
            console.error(`Erreur lors de la suppression de la référence ${refDoc.id}:`, error);
            // Continuer avec les autres références même si une échoue
          }
        }
      }
      
      console.log(`Suppression des références terminée`);
    } catch (error) {
      console.error(`Erreur lors de la suppression des références:`, error);
      // Continuer même si cette étape échoue
    }
    
    // 6. Supprimer le document utilisateur principal
    try {
      console.log(`Suppression du document utilisateur principal...`);
      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        console.log(`Document utilisateur ${uid} existe, suppression en cours...`);
        await deleteDoc(userRef);
        
        // Vérifier si le document a bien été supprimé
        const verifyDoc = await getDoc(userRef);
        if (!verifyDoc.exists()) {
          console.log(`Document utilisateur ${uid} supprimé avec succès (vérifié)`);
        } else {
          console.error(`Échec de la suppression du document utilisateur ${uid} (toujours présent après suppression)`);
          throw new Error(`Échec de la suppression du document utilisateur ${uid}`);
        }
      } else {
        console.log(`Document utilisateur ${uid} n'existe pas ou a déjà été supprimé`);
      }
    } catch (error) {
      console.error(`Erreur lors de la suppression du document utilisateur:`, error);
      throw error; // Cette étape est critique, donc on propage l'erreur
    }
    
    console.log(`Compte utilisateur ${uid} et toutes ses données associées supprimés avec succès`);
  } catch (error) {
    console.error(`Erreur lors de la suppression du compte ${uid}:`, error);
    throw error;
  }
};
